import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Alert,
  Container,
  Title,
  Text,
  Paper,
  Table,
  Group,
  Button,
  Badge,
  ActionIcon,
  Checkbox,
  Stack,
  Select,
  Pagination,
  Modal,
  Textarea,
  Skeleton,
  Avatar,
  ThemeIcon,
  TextInput,
  Divider,
  Image,
  Anchor,
  Box,
  Tooltip,
  Popover,
  ScrollArea,
  SimpleGrid,
  Code,
  Tabs,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconCheck,
  IconX,
  IconPhoto,
  IconRefresh,
  IconSearch,
  IconEye,
  IconExternalLink,
  IconCircleCheck,
  IconCircleX,
  IconArrowBackUp,
  IconUser,
  IconFlag,
  IconClipboardList,
  IconRobot,
  IconClock,
  IconSparkles,
} from "@tabler/icons-react";
import { Endpoints } from "../../../lib/endpoints";
import { PaginatedResponse } from "../../../lib/types";
import {
  usePerformRequest,
  PerformRequest,
} from "../../../hooks/usePerformRequest";
import { AdminProofPushAlerts } from "../../../components/AdminProofPushAlerts";

type ProofStatus = "pending" | "approved" | "rejected";

type PopulatedUser = {
  _id?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  level?: number;
};

type PopulatedMission = {
  _id?: string;
  title?: string;
  type?: string;
  points?: number;
  actionType?: string;
  description?: string;
  verificationInstruction?: string;
  proofInstructions?: string;
  platformUrl?: string;
  youtubeUrl?: string;
  spotifyUrl?: string;
  thumbnailUrl?: string;
  requiredListens?: number;
  status?: string;
  expiresAt?: string;
};

type ProofRow = {
  id: string;
  user: PopulatedUser;
  mission: PopulatedMission;
  platform?: string;
  proofUrl?: string;
  postLink?: string;
  status: ProofStatus;
  rejectionReason?: string;
  submittedAt?: string;
  createdAt?: string;
  reviewedAt?: string;
  reviewedBy?: PopulatedUser | null;
  aiScore?: number | null;
  aiConfidence?: string | null;
  aiDecision?: string | null;
  aiRiskFlags?: string[];
  aiEvidence?: string[];
  aiDecisionReason?: string;
  ocrAnchorScore?: number | null;
  ocrAnchorPassed?: boolean | null;
  socialVerification?: SocialVerif[];
};

type SocialVerif = {
  platform: string;
  status: string;
  handle?: string | null;
  profileUrl?: string | null;
};

function pickId(ref: unknown): string {
  if (ref == null) return "";
  if (typeof ref === "string") return ref;
  if (typeof ref === "object" && ref !== null && "_id" in ref) {
    return String((ref as { _id: unknown })._id);
  }
  return String(ref);
}

function normalizeUser(raw: unknown): PopulatedUser {
  if (raw && typeof raw === "object" && "email" in (raw as object)) {
    const u = raw as PopulatedUser;
    return {
      _id: u._id ? String(u._id) : undefined,
      email: u.email,
      firstName: u.firstName,
      lastName: u.lastName,
      username: u.username,
      level: typeof u.level === "number" ? u.level : undefined,
    };
  }
  const id = pickId(raw);
  return {
    _id: id || undefined,
    email: id ? `User ${id.slice(-6)}` : "—",
  };
}

function normalizeMission(raw: unknown): PopulatedMission {
  if (raw && typeof raw === "object" && "title" in (raw as object)) {
    const m = raw as PopulatedMission;
    return {
      _id: m._id ? String(m._id) : undefined,
      title: m.title,
      type: m.type,
      points: m.points,
      actionType: m.actionType,
      description: m.description,
      verificationInstruction: m.verificationInstruction,
      proofInstructions: m.proofInstructions,
      platformUrl: m.platformUrl,
      youtubeUrl: m.youtubeUrl,
      spotifyUrl: m.spotifyUrl,
      thumbnailUrl: m.thumbnailUrl,
      requiredListens: m.requiredListens,
      status: m.status,
      expiresAt: m.expiresAt,
    };
  }
  const id = pickId(raw);
  return {
    _id: id || undefined,
    title: id ? `Mission ${id.slice(-6)}` : "—",
  };
}

function proofDocumentId(raw: Record<string, unknown>): string {
  const id = raw._id ?? raw.id;
  if (id == null) return "";
  if (typeof id === "string") return id;
  if (typeof id === "object" && id !== null && "$oid" in id) {
    return String((id as { $oid: string }).$oid);
  }
  if (typeof id === "object" && id !== null && "_id" in id) {
    return proofDocumentId({ _id: (id as { _id: unknown })._id } as Record<string, unknown>);
  }
  if (typeof id === "object" && id !== null) {
    const s = (id as { toString?: () => string }).toString?.();
    if (s && !s.startsWith("[object ")) return s;
  }
  return "";
}

function normalizeProof(raw: Record<string, unknown>): ProofRow {
  const id = proofDocumentId(raw);
  return {
    id,
    user: normalizeUser(raw.userId ?? raw.user),
    mission: normalizeMission(raw.missionId ?? raw.mission),
    platform: (raw.platform as string) || undefined,
    proofUrl: (raw.proofUrl as string) || undefined,
    postLink: (raw.postLink as string) || undefined,
    status: (raw.status as ProofStatus) || "pending",
    rejectionReason: (raw.rejectionReason as string) || undefined,
    submittedAt: (raw.submittedAt as string) || (raw.createdAt as string),
    createdAt: raw.createdAt as string | undefined,
    reviewedAt: raw.reviewedAt as string | undefined,
    reviewedBy: raw.reviewedBy
      ? normalizeUser(raw.reviewedBy)
      : null,
    aiScore: raw.aiScore as number | null | undefined,
    aiConfidence: raw.aiConfidence as string | null | undefined,
    aiDecision: raw.aiDecision as string | null | undefined,
    aiRiskFlags: (raw.aiRiskFlags as string[]) || [],
    aiEvidence: (raw.aiEvidence as string[]) || [],
    aiDecisionReason: raw.aiDecisionReason as string | undefined,
    ocrAnchorScore: raw.ocrAnchorScore as number | null | undefined,
    ocrAnchorPassed: raw.ocrAnchorPassed as boolean | null | undefined,
    socialVerification: (raw.socialVerification as SocialVerif[]) || [],
  };
}

function userDisplayName(u: PopulatedUser) {
  const name = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  return name || u.username || u.email || "—";
}

function formatDate(value?: string | null) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return "—";
  }
}

function SectionCard({
  title,
  icon,
  children,
  gradient,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
  gradient: { from: string; to: string };
}) {
  return (
    <Paper
      radius="lg"
      p={0}
      withBorder
      style={{
        overflow: "hidden",
        borderColor: "var(--mantine-color-dark-4)",
      }}
    >
      <Box
        px="md"
        py="sm"
        style={{
          background: `linear-gradient(105deg, var(--mantine-color-${gradient.from}-8) 0%, var(--mantine-color-${gradient.to}-8) 100%)`,
        }}
      >
        <Group gap="sm">
          <ThemeIcon variant="transparent" color="white" size="sm">
            {icon}
          </ThemeIcon>
          <Text fw={700} size="sm" c="white">
            {title}
          </Text>
        </Group>
      </Box>
      <Box p="md">{children}</Box>
    </Paper>
  );
}

function submittedTimeMs(p: ProofRow): number {
  const raw = p.submittedAt || p.createdAt;
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
}

const ProofsPage = () => {
  useEffect(() => {
    document.title = "Proof Submissions | Virel Admin";
  }, []);

  const [statusFilter, setStatusFilter] = useState<ProofStatus | null>("pending");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [viewImage, setViewImage] = useState<string | null>(null);
  const [approvePopoverId, setApprovePopoverId] = useState<string | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPayload, setDetailPayload] = useState<{
    proof: Record<string, unknown>;
    attempt: Record<string, unknown> | null;
  } | null>(null);

  const [rejectionReason, setRejectionReason] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  // Reverse & deny (claw back money from an already-paid fraud proof)
  const [reverseModalOpen, setReverseModalOpen] = useState(false);
  const [reverseId, setReverseId] = useState<string | null>(null);
  const [reverseReason, setReverseReason] = useState("");
  const [reverseSuspend, setReverseSuspend] = useState(false);
  const [isReversing, setIsReversing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);

  // Bulk selection (pending proofs only)
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkRejectMode, setBulkRejectMode] = useState(false);
  const [bulkApprovePopover, setBulkApprovePopover] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<string | null>(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, debouncedSearch, pageSize]);

  useEffect(() => {
    setSelectedIds([]);
  }, [statusFilter, debouncedSearch, pageSize, currentPage, refreshKey]);

  const proofsEndpoint = useMemo(
    () =>
      Endpoints.GetProofs({
        page: currentPage,
        limit: pageSize,
        // When SEARCHING a specific person/mission, ignore the status filter and
        // show ALL their proofs (approved/rejected/pending). The page defaults to
        // the "pending" review queue, which otherwise hides an already-approved
        // submission and makes it look "missing" when you search for it.
        status: debouncedSearch.trim() ? undefined : statusFilter || undefined,
        search: debouncedSearch.trim() || undefined,
        sortBy: "submittedAt",
        sortDir: "desc",
      }),
    [currentPage, pageSize, statusFilter, refreshKey, debouncedSearch],
  );

  const { data, isLoading, error, reloadData } = usePerformRequest<
    PaginatedResponse<Record<string, unknown>[]>
  >({
    method: proofsEndpoint.method,
    route: proofsEndpoint.route,
    refreshKey: refreshKey,
  });

  // A failed/interrupted request leaves `data` undefined, which otherwise renders
  // identically to a genuinely-empty page ("No proof submissions found"). On a
  // flaky connection that makes real submissions look like they vanished. Detect
  // the failure so we can show a retry prompt instead of a false "none".
  const loadFailed = !isLoading && !!error && !data;

  const rawList = Array.isArray(data?.data) ? data!.data : [];
  const proofs = rawList
    .map((r) => normalizeProof(r))
    .sort((a, b) => {
      const tb = submittedTimeMs(b);
      const ta = submittedTimeMs(a);
      if (tb !== ta) return tb - ta;
      return b.id.localeCompare(a.id);
    });
  const totalRecords = data?.totalRecords ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize) || 1);

  const loadDetail = useCallback(async (id: string) => {
    setDetailLoading(true);
    setDetailPayload(null);
    try {
      const ep = Endpoints.GetProofById(id);
      const res = await PerformRequest<{ data?: { proof: Record<string, unknown>; attempt: Record<string, unknown> | null } }>({
        method: ep.method,
        route: ep.route,
      });
      if (res.status == null || res.status >= 400) {
        throw new Error(
          (res as { message?: string }).message || "Failed to load proof",
        );
      }
      const body = res.data as { data?: { proof: Record<string, unknown>; attempt: Record<string, unknown> | null } };
      const inner = body?.data;
      if (!inner?.proof) throw new Error("Invalid response");
      setDetailPayload({ proof: inner.proof, attempt: inner.attempt ?? null });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Could not load details";
      notifications.show({ title: "Error", message: msg, color: "red" });
      setDetailModalOpen(false);
      setDetailId(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (detailModalOpen && detailId) {
      void loadDetail(detailId);
    }
  }, [detailModalOpen, detailId, loadDetail]);

  const openDetail = (id: string) => {
    setDetailId(id);
    setDetailModalOpen(true);
  };

  const closeDetail = () => {
    setDetailModalOpen(false);
    setDetailId(null);
    setDetailPayload(null);
  };

  const handleApprove = async (id: string) => {
    setIsProcessing(true);
    try {
      const endpoint = Endpoints.ApproveProof(id);
      const res = await PerformRequest({
        method: endpoint.method,
        route: endpoint.route,
        data: { status: "approved" },
      });
      if (!res.status || res.status >= 400) {
        const msg =
          (res as { message?: string }).message ||
          (res.data as { message?: string } | undefined)?.message ||
          "Approval failed";
        throw new Error(msg);
      }
      notifications.show({
        title: "Approved",
        message: "Proof was approved successfully.",
        color: "teal",
      });
      setRefreshKey((k) => k + 1);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Approval failed";
      notifications.show({ title: "Error", message: msg, color: "red" });
    } finally {
      setIsProcessing(false);
    }
  };

  const openReject = (id: string) => {
    setSelectedId(id);
    setRejectionReason("");
    setRejectModalOpen(true);
  };

  const handleReject = async () => {
    if (!selectedId || !rejectionReason.trim()) return;
    setIsProcessing(true);
    try {
      const endpoint = Endpoints.RejectProof(selectedId);
      const res = await PerformRequest({
        method: endpoint.method,
        route: endpoint.route,
        data: { status: "rejected", rejectionReason: rejectionReason.trim() },
      });
      if (!res.status || res.status >= 400) {
        const msg =
          (res as { message?: string }).message ||
          (res.data as { message?: string } | undefined)?.message ||
          "Rejection failed";
        throw new Error(msg);
      }
      notifications.show({
        title: "Rejected",
        message: "The user will be notified with your reason.",
        color: "orange",
      });
      setRejectModalOpen(false);
      setSelectedId(null);
      setRejectionReason("");
      setRefreshKey((k) => k + 1);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Rejection failed";
      notifications.show({ title: "Error", message: msg, color: "red" });
    } finally {
      setIsProcessing(false);
    }
  };

  const openReverse = (id: string) => {
    setReverseId(id);
    setReverseReason("");
    setReverseSuspend(false);
    setReverseModalOpen(true);
  };

  const handleReverse = async () => {
    if (!reverseId || !reverseReason.trim()) return;
    setIsReversing(true);
    try {
      const endpoint = Endpoints.ReverseProof(reverseId);
      const res = await PerformRequest<{
        clawed: number;
        newBalance: number;
        strikes: number;
        suspended: boolean;
        alreadyReversed?: boolean;
      }>({
        method: endpoint.method,
        route: endpoint.route,
        data: { reason: reverseReason.trim(), alsoSuspend: reverseSuspend },
      });
      if (!res.status || res.status >= 400) {
        const msg =
          (res as { message?: string }).message ||
          (res.data as { message?: string } | undefined)?.message ||
          "Reversal failed";
        throw new Error(msg);
      }
      const d = res.data;
      notifications.show({
        title: d?.alreadyReversed ? "Already reversed" : "Proof reversed",
        message: d?.alreadyReversed
          ? "This proof was already reversed — no change."
          : `Clawed back ₦${d?.clawed ?? 0}. New balance ₦${d?.newBalance ?? 0}${(d?.newBalance ?? 0) < 0 ? " (owing)" : ""}. Strike ${d?.strikes ?? 0}${d?.suspended ? " — account suspended." : "."}`,
        color: d?.suspended ? "red" : "orange",
        autoClose: 8000,
      });
      setReverseModalOpen(false);
      setReverseId(null);
      setReverseReason("");
      setReverseSuspend(false);
      setRefreshKey((k) => k + 1);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Reversal failed";
      notifications.show({ title: "Error", message: msg, color: "red" });
    } finally {
      setIsReversing(false);
    }
  };

  const runBulk = async (
    ids: string[],
    action: "approve" | "reject",
    reason?: string,
  ) => {
    setIsProcessing(true);
    let ok = 0;
    const failures: string[] = [];
    for (let i = 0; i < ids.length; i++) {
      setBulkProgress(`${action === "approve" ? "Approving" : "Rejecting"} ${i + 1} of ${ids.length}…`);
      try {
        const endpoint =
          action === "approve"
            ? Endpoints.ApproveProof(ids[i])
            : Endpoints.RejectProof(ids[i]);
        const res = await PerformRequest({
          method: endpoint.method,
          route: endpoint.route,
          data:
            action === "approve"
              ? { status: "approved" }
              : { status: "rejected", rejectionReason: reason },
        });
        if (!res.status || res.status >= 400) {
          throw new Error(
            (res as { message?: string }).message ||
              (res.data as { message?: string } | undefined)?.message ||
              "Request failed",
          );
        }
        ok++;
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : "failed";
        failures.push(msg);
      }
    }
    setBulkProgress(null);
    setIsProcessing(false);
    setSelectedIds([]);
    setRefreshKey((k) => k + 1);
    if (failures.length === 0) {
      notifications.show({
        title: action === "approve" ? "Approved" : "Rejected",
        message: `${ok} proof${ok === 1 ? "" : "s"} ${action === "approve" ? "approved" : "rejected"} successfully.`,
        color: action === "approve" ? "teal" : "orange",
      });
    } else {
      notifications.show({
        title: "Finished with errors",
        message: `${ok} succeeded, ${failures.length} failed (${failures[0]}). The failed ones are still pending — try them again.`,
        color: "yellow",
        autoClose: 8000,
      });
    }
  };

  const getStatusBadge = (status: ProofStatus) => {
    const config: Record<ProofStatus, { color: string; label: string }> = {
      pending: { color: "yellow", label: "Pending" },
      approved: { color: "green", label: "Approved" },
      rejected: { color: "red", label: "Rejected" },
    };
    const c = config[status];
    return (
      <Badge color={c.color} variant="light" size="sm">
        {c.label}
      </Badge>
    );
  };

  const aiSummary = (p: ProofRow) => {
    if (p.aiScore == null && !p.aiDecision) return "—";
    const parts: string[] = [];
    if (p.aiScore != null) parts.push(`${p.aiScore}`);
    if (p.aiConfidence) parts.push(p.aiConfidence);
    if (p.aiDecision) parts.push(p.aiDecision.replace(/_/g, " "));
    return parts.join(" · ");
  };

  const colCount = 12;

  const pendingOnPage = proofs.filter((p) => p.status === "pending");
  const allPendingSelected =
    pendingOnPage.length > 0 &&
    pendingOnPage.every((p) => selectedIds.includes(p.id));

  const toggleSelect = (id: string) =>
    setSelectedIds((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
    );

  const rows = proofs.map((p) => (
    <Table.Tr key={p.id} bg={selectedIds.includes(p.id) ? "var(--mantine-color-teal-light)" : undefined}>
      <Table.Td>
        {p.status === "pending" ? (
          <Checkbox
            checked={selectedIds.includes(p.id)}
            onChange={() => toggleSelect(p.id)}
            disabled={isProcessing}
            aria-label="Select proof"
          />
        ) : null}
      </Table.Td>
      <Table.Td>
        <Group gap="sm" wrap="nowrap">
          <Avatar size="sm" color="teal" radius="sm" src={undefined}>
            {(userDisplayName(p.user) || "?").charAt(0).toUpperCase()}
          </Avatar>
          <div style={{ minWidth: 0 }}>
            <Text fw={500} size="sm" lineClamp={1}>
              {userDisplayName(p.user)}
            </Text>
            <Text size="xs" c="dimmed" lineClamp={1}>
              {p.user.email}
              {p.user.username ? ` · @${p.user.username}` : ""}
              {" · "}
              Level {p.user.level ?? 1}
            </Text>
            {p.socialVerification && p.socialVerification.length > 0 ? (
              <Group gap={4} mt={4}>
                {p.socialVerification.map((s) => {
                  const label =
                    s.platform === "twitter"
                      ? "X"
                      : s.platform === "tiktok"
                        ? "TikTok"
                        : "IG";
                  const color =
                    s.status === "verified"
                      ? "green"
                      : s.status === "manual_review"
                        ? "orange"
                        : s.status === "rejected"
                          ? "red"
                          : "gray";
                  const mark =
                    s.status === "verified"
                      ? " ✓"
                      : s.status === "manual_review"
                        ? " ⏳"
                        : "";
                  const badge = (
                    <Badge
                      size="xs"
                      variant="light"
                      color={color}
                      style={s.profileUrl ? { cursor: "pointer" } : undefined}
                      title={`${label}: ${s.status}${s.handle ? " @" + s.handle : ""}${s.profileUrl ? " — click to open profile" : ""}`}
                    >
                      {label}
                      {mark}
                    </Badge>
                  );
                  return s.profileUrl ? (
                    <a
                      key={s.platform}
                      href={s.profileUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{ textDecoration: "none" }}
                    >
                      {badge}
                    </a>
                  ) : (
                    <span key={s.platform}>{badge}</span>
                  );
                })}
              </Group>
            ) : null}
          </div>
        </Group>
      </Table.Td>
      <Table.Td>
        <Text size="sm" fw={500} lineClamp={2}>
          {p.mission.title}
        </Text>
        <Group gap={4} mt={4}>
          <Badge size="xs" variant="outline" color="gray">
            {p.mission.type || "—"}
          </Badge>
          {p.mission.actionType ? (
            <Badge size="xs" variant="light" color="blue">
              {p.mission.actionType}
            </Badge>
          ) : null}
        </Group>
      </Table.Td>
      <Table.Td>
        <Text size="sm" fw={600}>
          {p.mission.points != null ? p.mission.points : "—"}
        </Text>
      </Table.Td>
      <Table.Td>
        <Badge size="sm" variant="light" color="grape">
          {p.platform || "—"}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          {p.proofUrl && (
            <Tooltip label="View proof image">
              <ActionIcon
                variant="light"
                color="blue"
                onClick={() => setViewImage(p.proofUrl!)}
              >
                <IconPhoto size={16} />
              </ActionIcon>
            </Tooltip>
          )}
          {p.postLink && (
            <Tooltip label="Open post link">
              <ActionIcon
                variant="light"
                color="gray"
                component="a"
                href={p.postLink}
                target="_blank"
                rel="noreferrer"
              >
                <IconExternalLink size={16} />
              </ActionIcon>
            </Tooltip>
          )}
          {!p.proofUrl && !p.postLink ? (
            <Text size="xs" c="dimmed">
              —
            </Text>
          ) : null}
        </Group>
      </Table.Td>
      <Table.Td>
        <Text size="xs" lineClamp={2} maw={140}>
          {aiSummary(p)}
        </Text>
        {p.ocrAnchorPassed != null && (
          <Badge size="xs" mt={4} color={p.ocrAnchorPassed ? "teal" : "orange"} variant="dot">
            OCR {p.ocrAnchorPassed ? "pass" : "fail"}
          </Badge>
        )}
      </Table.Td>
      <Table.Td>{getStatusBadge(p.status)}</Table.Td>
      <Table.Td>
        <Text size="xs" maw={120}>
          {formatDate(p.submittedAt)}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="xs" maw={120}>
          {formatDate(p.reviewedAt)}
        </Text>
        {p.reviewedBy?.email && (
          <Text size="xs" c="dimmed" lineClamp={1}>
            {userDisplayName(p.reviewedBy)}
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Text size="xs" lineClamp={2} maw={160} c={p.rejectionReason ? "red" : "dimmed"}>
          {p.rejectionReason || "—"}
        </Text>
      </Table.Td>
      <Table.Td>
        <Group gap={6} wrap="nowrap">
          <Tooltip label="View full details">
            <ActionIcon variant="light" color="cyan" onClick={() => openDetail(p.id)}>
              <IconEye size={18} />
            </ActionIcon>
          </Tooltip>
          {p.status === "pending" && (
            <>
              <Popover
                opened={approvePopoverId === p.id}
                onClose={() => setApprovePopoverId(null)}
                position="bottom-end"
                withArrow
                shadow="md"
                width={280}
                withinPortal
              >
                <Popover.Target>
                  <Tooltip label="Approve">
                    <ActionIcon
                      variant="light"
                      color="green"
                      loading={isProcessing && approvePopoverId === p.id}
                      onClick={() =>
                        setApprovePopoverId((cur) => (cur === p.id ? null : p.id))
                      }
                    >
                      <IconCircleCheck size={18} />
                    </ActionIcon>
                  </Tooltip>
                </Popover.Target>
                <Popover.Dropdown>
                  <Stack gap="sm">
                    <Group gap="xs">
                      <ThemeIcon color="green" variant="light" size="lg" radius="md">
                        <IconSparkles size={18} />
                      </ThemeIcon>
                      <div>
                        <Text size="sm" fw={700}>
                          Confirm approval
                        </Text>
                        <Text size="xs" c="dimmed">
                          Award mission points and mark the attempt complete.
                        </Text>
                      </div>
                    </Group>
                    <Group justify="flex-end" gap="xs">
                      <Button size="xs" variant="default" onClick={() => setApprovePopoverId(null)}>
                        Cancel
                      </Button>
                      <Button
                        size="xs"
                        color="green"
                        leftSection={<IconCheck size={14} />}
                        loading={isProcessing}
                        onClick={async () => {
                          await handleApprove(p.id);
                          setApprovePopoverId(null);
                        }}
                      >
                        Approve
                      </Button>
                    </Group>
                  </Stack>
                </Popover.Dropdown>
              </Popover>
              <Tooltip label="Reject">
                <ActionIcon
                  variant="light"
                  color="red"
                  onClick={() => openReject(p.id)}
                  disabled={isProcessing}
                >
                  <IconCircleX size={18} />
                </ActionIcon>
              </Tooltip>
            </>
          )}
          {p.status === "approved" && (
            <Tooltip label="Reverse & deny (claw back money)">
              <ActionIcon
                variant="light"
                color="orange"
                onClick={() => openReverse(p.id)}
              >
                <IconArrowBackUp size={18} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  const skeletonRows = Array.from({ length: 5 }).map((_, idx) => (
    <Table.Tr key={`skeleton-${idx}`}>
      {Array.from({ length: colCount }).map((__, c) => (
        <Table.Td key={c}>
          <Skeleton height={28} />
        </Table.Td>
      ))}
    </Table.Tr>
  ));

  const selectedRow = selectedId ? proofs.find((x) => x.id === selectedId) : null;

  const detailProof = detailPayload?.proof
    ? normalizeProof(detailPayload.proof as Record<string, unknown>)
    : null;
  const detailAttempt = detailPayload?.attempt;

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={1}>Proof Submissions</Title>
            <Text size="sm" c="dimmed">
              Manually review and verify user proof submissions
            </Text>
          </div>
          <Button
            variant="default"
            leftSection={<IconRefresh size={16} />}
            onClick={() => setRefreshKey((k) => k + 1)}
          >
            Refresh
          </Button>
        </Group>

        <AdminProofPushAlerts />

        <Paper withBorder p="md" radius="md">
          <Group mb="md" align="flex-end" wrap="wrap">
            <TextInput
              placeholder="Search by user or mission..."
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              style={{ flex: 1, minWidth: 220 }}
            />
            <Select
              label="Status"
              placeholder="All statuses"
              data={[
                { value: "pending", label: "Pending" },
                { value: "approved", label: "Approved" },
                { value: "rejected", label: "Rejected" },
              ]}
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as ProofStatus)}
              clearable
              w={160}
            />
            <Select
              label="Rows per page"
              data={[
                { value: "10", label: "10" },
                { value: "25", label: "25" },
                { value: "50", label: "50" },
              ]}
              value={String(pageSize)}
              onChange={(v) => setPageSize(Number(v) || 10)}
              w={120}
            />
          </Group>

          {selectedIds.length > 0 && (
            <Paper withBorder p="sm" radius="md" mb="md" bg="var(--mantine-color-teal-light)">
              <Group justify="space-between" wrap="wrap">
                <Text size="sm" fw={600}>
                  {bulkProgress || `${selectedIds.length} proof${selectedIds.length === 1 ? "" : "s"} selected`}
                </Text>
                <Group gap="xs">
                  <Popover
                    opened={bulkApprovePopover}
                    onClose={() => setBulkApprovePopover(false)}
                    position="bottom-end"
                    withArrow
                    shadow="md"
                    width={300}
                    withinPortal
                  >
                    <Popover.Target>
                      <Button
                        size="xs"
                        color="green"
                        leftSection={<IconCircleCheck size={14} />}
                        loading={isProcessing}
                        onClick={() => setBulkApprovePopover(true)}
                      >
                        Approve selected
                      </Button>
                    </Popover.Target>
                    <Popover.Dropdown>
                      <Stack gap="sm">
                        <Text size="sm" fw={700}>
                          Approve {selectedIds.length} proof{selectedIds.length === 1 ? "" : "s"}?
                        </Text>
                        <Text size="xs" c="dimmed">
                          Each user gets their points and the attempt is marked
                          complete — same as approving them one by one.
                        </Text>
                        <Group justify="flex-end" gap="xs">
                          <Button size="xs" variant="default" onClick={() => setBulkApprovePopover(false)}>
                            Cancel
                          </Button>
                          <Button
                            size="xs"
                            color="green"
                            leftSection={<IconCheck size={14} />}
                            loading={isProcessing}
                            onClick={async () => {
                              setBulkApprovePopover(false);
                              await runBulk(selectedIds, "approve");
                            }}
                          >
                            Yes, approve all
                          </Button>
                        </Group>
                      </Stack>
                    </Popover.Dropdown>
                  </Popover>
                  <Button
                    size="xs"
                    color="red"
                    variant="light"
                    leftSection={<IconCircleX size={14} />}
                    disabled={isProcessing}
                    onClick={() => {
                      setBulkRejectMode(true);
                      setRejectionReason("");
                      setRejectModalOpen(true);
                    }}
                  >
                    Reject selected
                  </Button>
                  <Button size="xs" variant="default" disabled={isProcessing} onClick={() => setSelectedIds([])}>
                    Clear
                  </Button>
                </Group>
              </Group>
            </Paper>
          )}

          <Table.ScrollContainer minWidth={1100}>
            <Table striped highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={40}>
                    <Checkbox
                      checked={allPendingSelected}
                      indeterminate={selectedIds.length > 0 && !allPendingSelected}
                      onChange={() =>
                        setSelectedIds(
                          allPendingSelected ? [] : pendingOnPage.map((p) => p.id),
                        )
                      }
                      disabled={isProcessing || pendingOnPage.length === 0}
                      aria-label="Select all pending on this page"
                    />
                  </Table.Th>
                  <Table.Th>User</Table.Th>
                  <Table.Th>Mission</Table.Th>
                  <Table.Th>Points</Table.Th>
                  <Table.Th>Platform</Table.Th>
                  <Table.Th>Proof</Table.Th>
                  <Table.Th>AI / OCR</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Submitted</Table.Th>
                  <Table.Th>Reviewed</Table.Th>
                  <Table.Th>Rejection</Table.Th>
                  <Table.Th w={140}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {isLoading ? (
                  skeletonRows
                ) : rows.length > 0 ? (
                  rows
                ) : loadFailed ? (
                  <Table.Tr>
                    <Table.Td colSpan={colCount} style={{ textAlign: "center" }}>
                      <Stack align="center" gap="xs" py="xl">
                        <Text c="orange" fw={500}>
                          Couldn&apos;t load submissions
                        </Text>
                        <Text c="dimmed" size="sm">
                          This is a connection issue, not missing data — your
                          submissions are safe. Tap retry.
                        </Text>
                        <Button
                          size="xs"
                          variant="light"
                          onClick={() => reloadData()}
                        >
                          Retry
                        </Button>
                      </Stack>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  <Table.Tr>
                    <Table.Td colSpan={colCount} style={{ textAlign: "center" }}>
                      <Text c="dimmed" py="xl">
                        No proof submissions found
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>

          <Group justify="space-between" mt="md" wrap="wrap">
            <Text size="sm" c="dimmed">
              {totalRecords === 0
                ? "No records"
                : `Showing ${(currentPage - 1) * pageSize + 1}–${Math.min(
                    currentPage * pageSize,
                    totalRecords,
                  )} of ${totalRecords}`}
            </Text>
            <Pagination
              value={currentPage}
              onChange={setCurrentPage}
              total={totalPages}
              disabled={totalRecords === 0}
            />
          </Group>
        </Paper>
      </Stack>

      {/* zIndex above the detail modal (default 200) so the proof image opens
          ON TOP of it — reviewer can see the image without closing the modal. */}
      <Modal
        opened={!!viewImage}
        onClose={() => setViewImage(null)}
        title="Proof image"
        size="xl"
        centered
        zIndex={2000}
      >
        {viewImage ? <Image src={viewImage} radius="md" alt="Proof" /> : null}
      </Modal>

      {/* Reverse & deny — claw back money from an already-paid fraud proof */}
      <Modal
        opened={reverseModalOpen}
        onClose={() => {
          setReverseModalOpen(false);
          setReverseId(null);
          setReverseReason("");
          setReverseSuspend(false);
        }}
        title="Reverse & deny — claw back money"
        size="md"
        centered
      >
        <Stack gap="md">
          <Alert
            color="orange"
            variant="light"
            icon={<IconArrowBackUp size={16} />}
          >
            This denies the proof and claws back its money. If the member already
            withdrew it, their balance goes <b>negative (they owe us)</b> —
            future earnings pay it down and they can't withdraw until it clears.
          </Alert>
          <Textarea
            label="Reason (required)"
            placeholder="e.g. Fake / edited screenshot the AI missed"
            minRows={3}
            value={reverseReason}
            onChange={(e) => setReverseReason(e.currentTarget.value)}
            required
          />
          <Checkbox
            label="Also suspend this account now"
            checked={reverseSuspend}
            onChange={(e) => setReverseSuspend(e.currentTarget.checked)}
          />
          <Text size="xs" c="dimmed">
            3 reversals auto-suspend the account. A Telegram alert fires on every
            reversal.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setReverseModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              color="orange"
              leftSection={<IconArrowBackUp size={16} />}
              loading={isReversing}
              disabled={!reverseReason.trim()}
              onClick={handleReverse}
            >
              Reverse &amp; claw back
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={rejectModalOpen}
        onClose={() => {
          setRejectModalOpen(false);
          setSelectedId(null);
          setBulkRejectMode(false);
          setRejectionReason("");
        }}
        title={
          <Group gap="sm">
            <ThemeIcon color="red" variant="light" radius="md">
              <IconCircleX size={20} />
            </ThemeIcon>
            <Text fw={700}>
              {bulkRejectMode
                ? `Reject ${selectedIds.length} submission${selectedIds.length === 1 ? "" : "s"}`
                : "Reject submission"}
            </Text>
          </Group>
        }
        size="md"
        centered
        yOffset="1.25rem"
        styles={{
          content: {
            maxHeight: "calc(100dvh - 2rem)",
            display: "flex",
            flexDirection: "column",
          },
          body: {
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            paddingTop: "var(--mantine-spacing-xs)",
            paddingBottom: "var(--mantine-spacing-md)",
          },
        }}
      >
        <Stack
          gap={0}
          style={{
            flex: 1,
            minHeight: 0,
            maxHeight: "calc(100dvh - 9rem)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ScrollArea
            type="auto"
            offsetScrollbars
            scrollbarSize={8}
            style={{ flex: "1 1 auto", minHeight: 0 }}
            mah="calc(100dvh - 14rem)"
          >
            <Stack gap="md" pr="xs" pb="xs">
              {selectedRow && (
                <Paper withBorder p="md" radius="md" variant="light" color="gray">
                  <Stack gap={6}>
                    <Text size="sm" fw={600}>
                      {userDisplayName(selectedRow.user)}
                    </Text>
                    {selectedRow.user.email ? (
                      <Text size="xs" c="dimmed">
                        {selectedRow.user.email}
                      </Text>
                    ) : null}
                    <Divider my={4} />
                    <Text size="sm" fw={500}>
                      {selectedRow.mission.title?.trim()
                        ? selectedRow.mission.title
                        : "Mission (title unavailable)"}
                    </Text>
                    <Group gap="xs">
                      {selectedRow.platform ? (
                        <Badge size="xs" variant="outline">
                          {selectedRow.platform}
                        </Badge>
                      ) : null}
                      <Text size="xs" c="dimmed">
                        Submitted {formatDate(selectedRow.submittedAt)}
                      </Text>
                    </Group>
                  </Stack>
                </Paper>
              )}
              <Text size="sm" c="dimmed">
                {bulkRejectMode
                  ? "Provide a clear reason. The SAME reason will be sent to every selected user — only bulk-reject submissions that fail for the same reason."
                  : "Provide a clear reason. It will be shown to the user in their notification."}
              </Text>
              <Textarea
                placeholder="Rejection reason (required)…"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.currentTarget.value)}
                minRows={4}
                maxRows={6}
                autosize
                data-autofocus
              />
            </Stack>
          </ScrollArea>
          <Box
            pt="md"
            mt="xs"
            style={{
              flexShrink: 0,
              borderTop: "1px solid var(--mantine-color-default-border)",
            }}
          >
            <Group justify="flex-end" gap="sm" wrap="nowrap">
              <Button
                variant="default"
                onClick={() => {
                  setRejectModalOpen(false);
                  setSelectedId(null);
                  setBulkRejectMode(false);
                  setRejectionReason("");
                }}
              >
                Cancel
              </Button>
              <Button
                color="red"
                leftSection={<IconCircleX size={16} />}
                onClick={async () => {
                  if (bulkRejectMode) {
                    const reason = rejectionReason.trim();
                    setRejectModalOpen(false);
                    setBulkRejectMode(false);
                    setRejectionReason("");
                    await runBulk(selectedIds, "reject", reason);
                  } else {
                    await handleReject();
                  }
                }}
                loading={isProcessing}
                disabled={!rejectionReason.trim()}
              >
                Confirm rejection
              </Button>
            </Group>
          </Box>
        </Stack>
      </Modal>

      <Modal
        opened={detailModalOpen}
        onClose={closeDetail}
        title={null}
        withCloseButton={false}
        padding={0}
        size="xl"
        radius="lg"
        centered
        yOffset="1rem"
        styles={{
          content: {
            maxHeight: "calc(100dvh - 2rem)",
            display: "flex",
            flexDirection: "column",
            padding: 0,
          },
          body: {
            padding: 0,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
          header: {
            display: "none",
          },
        }}
      >
        <Stack
          gap={0}
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            style={{
              flexShrink: 0,
              background:
                "linear-gradient(135deg, rgba(18, 184, 134, 0.15) 0%, rgba(34, 139, 230, 0.12) 45%, rgba(121, 80, 242, 0.1) 100%)",
              borderBottom: "1px solid var(--mantine-color-dark-4)",
            }}
            px="xl"
            py="lg"
          >
          <Group justify="space-between" align="flex-start">
            <div>
              <Group gap="xs" mb={6}>
                <ThemeIcon size="lg" radius="md" variant="gradient" gradient={{ from: "teal", to: "blue", deg: 120 }}>
                  <IconClipboardList size={22} />
                </ThemeIcon>
                <Title order={3}>Submission detail</Title>
              </Group>
              <Text size="sm" c="dimmed">
                Mission, attempt, AI signals, and proof artifacts in one place.
              </Text>
            </div>
            <Button variant="light" color="gray" size="xs" onClick={closeDetail}>
              Close
            </Button>
          </Group>
        </Box>

        <Tabs
          key={detailId ?? "detail"}
          defaultValue="submission"
          variant="outline"
          radius="md"
          keepMounted={false}
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
          styles={{
            list: {
              flexShrink: 0,
              paddingLeft: "var(--mantine-spacing-md)",
              paddingRight: "var(--mantine-spacing-md)",
              paddingTop: "var(--mantine-spacing-sm)",
              borderBottom: "1px solid var(--mantine-color-default-border)",
            },
            panel: {
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <Tabs.List>
            <Tabs.Tab value="submission" leftSection={<IconPhoto size={16} />}>
              Submission
            </Tabs.Tab>
            <Tabs.Tab value="user" leftSection={<IconUser size={16} />}>
              User
            </Tabs.Tab>
            <Tabs.Tab value="mission" leftSection={<IconFlag size={16} />}>
              Mission
            </Tabs.Tab>
            <Tabs.Tab value="attempt" leftSection={<IconClock size={16} />}>
              Attempt
            </Tabs.Tab>
            <Tabs.Tab value="ai" leftSection={<IconRobot size={16} />}>
              AI & OCR
            </Tabs.Tab>
          </Tabs.List>

          {detailLoading && (
            <Box p="md" style={{ flex: 1 }}>
              <Stack gap="sm">
                <Skeleton height={120} radius="md" />
                <Skeleton height={200} radius="md" />
                <Skeleton height={160} radius="md" />
              </Stack>
            </Box>
          )}

          {!detailLoading && detailProof && (
            <>
              <Tabs.Panel value="submission" pt="md" px="md" pb="md" style={{ flex: 1, minHeight: 0 }}>
                <ScrollArea h="calc(100dvh - 15rem)" type="auto" offsetScrollbars>
                  <SectionCard title="Submission" icon={<IconPhoto size={16} />} gradient={{ from: "blue", to: "cyan" }}>
                    <Stack gap="sm">
                      <Group gap="xs">
                        <Badge>{detailProof.status}</Badge>
                        <Badge variant="light" color="grape">
                          {detailProof.platform || "platform"}
                        </Badge>
                      </Group>
                      <Divider label="Timestamps" labelPosition="left" />
                      <SimpleGrid cols={2} spacing="xs">
                        <div>
                          <Text size="xs" c="dimmed">
                            Submitted
                          </Text>
                          <Text size="sm">{formatDate(detailProof.submittedAt)}</Text>
                        </div>
                        <div>
                          <Text size="xs" c="dimmed">
                            Reviewed
                          </Text>
                          <Text size="sm">{formatDate(detailProof.reviewedAt)}</Text>
                        </div>
                      </SimpleGrid>
                      {detailProof.rejectionReason && (
                        <Alert color="red" title="Rejection Reason" mt="sm" variant="light">
                          {detailProof.rejectionReason}
                        </Alert>
                      )}
                      <Group>
                        {detailProof.proofUrl && (
                          <Button
                            size="xs"
                            variant="light"
                            leftSection={<IconPhoto size={14} />}
                            onClick={() => setViewImage(detailProof.proofUrl!)}
                          >
                            Open proof image
                          </Button>
                        )}
                        {detailProof.postLink && (
                          <Button
                            size="xs"
                            variant="light"
                            component="a"
                            href={detailProof.postLink}
                            target="_blank"
                            rel="noreferrer"
                            leftSection={<IconExternalLink size={14} />}
                          >
                            Post link
                          </Button>
                        )}
                      </Group>
                    </Stack>
                  </SectionCard>
                </ScrollArea>
              </Tabs.Panel>

              <Tabs.Panel value="user" pt="md" px="md" pb="md" style={{ flex: 1, minHeight: 0 }}>
                <ScrollArea h="calc(100dvh - 15rem)" type="auto" offsetScrollbars>
                  <SectionCard title="User" icon={<IconUser size={16} />} gradient={{ from: "teal", to: "green" }}>
                    <Stack gap="xs">
                      <Text fw={700}>{userDisplayName(detailProof.user)}</Text>
                      <Text size="sm" c="dimmed">
                        {detailProof.user.email}
                      </Text>
                      {detailProof.user.username && (
                        <Text size="sm">
                          @{detailProof.user.username}
                        </Text>
                      )}
                      <Divider />
                      <Text size="xs" c="dimmed">
                        User ID
                      </Text>
                      <Code block>{detailProof.user._id || "—"}</Code>
                      <Divider label="Level" labelPosition="left" />
                      <Badge color="yellow" variant="light" size="lg">
                        Level {detailProof.user.level ?? 1}
                      </Badge>
                    </Stack>
                  </SectionCard>
                </ScrollArea>
              </Tabs.Panel>

              <Tabs.Panel value="mission" pt="md" px="md" pb="md" style={{ flex: 1, minHeight: 0 }}>
                <ScrollArea h="calc(100dvh - 15rem)" type="auto" offsetScrollbars>
                  <SectionCard title="Mission" icon={<IconFlag size={16} />} gradient={{ from: "violet", to: "grape" }}>
                    <Stack gap="sm">
                      <Title order={4}>{detailProof.mission.title}</Title>
                      <Group gap="xs">
                        <Badge color="blue">{detailProof.mission.type}</Badge>
                        {detailProof.mission.actionType && (
                          <Badge variant="outline">{detailProof.mission.actionType}</Badge>
                        )}
                        <Badge color="teal" variant="light">
                          {detailProof.mission.points ?? 0} pts
                        </Badge>
                        {detailProof.mission.status && (
                          <Badge variant="dot" color="gray">
                            {detailProof.mission.status}
                          </Badge>
                        )}
                      </Group>
                      {detailProof.mission.description && (
                        <>
                          <Text size="xs" c="dimmed" fw={600} mt="xs">
                            Description
                          </Text>
                          <Text size="sm">{detailProof.mission.description}</Text>
                        </>
                      )}
                      {(detailProof.mission.proofInstructions || detailProof.mission.verificationInstruction) && (
                        <>
                          <Text size="xs" c="dimmed" fw={600} mt="xs">
                            Instructions to user
                          </Text>
                          <Text size="sm">
                            {detailProof.mission.proofInstructions || detailProof.mission.verificationInstruction}
                          </Text>
                        </>
                      )}
                      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" mt="xs">
                        {detailProof.mission.platformUrl && (
                          <Anchor href={detailProof.mission.platformUrl} target="_blank" size="sm">
                            Platform URL
                          </Anchor>
                        )}
                        {detailProof.mission.youtubeUrl && (
                          <Anchor href={detailProof.mission.youtubeUrl} target="_blank" size="sm">
                            YouTube
                          </Anchor>
                        )}
                        {detailProof.mission.spotifyUrl && (
                          <Anchor href={detailProof.mission.spotifyUrl} target="_blank" size="sm">
                            Spotify
                          </Anchor>
                        )}
                      </SimpleGrid>
                      <Text size="xs" c="dimmed">
                        Mission ID
                      </Text>
                      <Code block>{detailProof.mission._id || "—"}</Code>
                    </Stack>
                  </SectionCard>
                </ScrollArea>
              </Tabs.Panel>

              <Tabs.Panel value="attempt" pt="md" px="md" pb="md" style={{ flex: 1, minHeight: 0 }}>
                <ScrollArea h="calc(100dvh - 15rem)" type="auto" offsetScrollbars>
                  <SectionCard title="Attempt" icon={<IconClock size={16} />} gradient={{ from: "orange", to: "red" }}>
                    {detailAttempt ? (
                      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                        <div>
                          <Text size="xs" c="dimmed">
                            Status
                          </Text>
                          <Badge mt={4}>{String(detailAttempt.status ?? "—")}</Badge>
                        </div>
                        <div>
                          <Text size="xs" c="dimmed">
                            Listen count
                          </Text>
                          <Text size="sm" mt={4}>
                            {detailAttempt.listenCount != null ? String(detailAttempt.listenCount) : "—"}
                          </Text>
                        </div>
                        <div>
                          <Text size="xs" c="dimmed">
                            Started
                          </Text>
                          <Text size="sm">{formatDate(detailAttempt.startedAt as string)}</Text>
                        </div>
                        <div>
                          <Text size="xs" c="dimmed">
                            Completed
                          </Text>
                          <Text size="sm">{formatDate(detailAttempt.completedAt as string)}</Text>
                        </div>
                        {detailAttempt.comment ? (
                          <div style={{ gridColumn: "1 / -1" }}>
                            <Text size="xs" c="dimmed">
                              User comment
                            </Text>
                            <Text size="sm" mt={4}>
                              {String(detailAttempt.comment)}
                            </Text>
                          </div>
                        ) : null}
                        <div style={{ gridColumn: "1 / -1" }}>
                          <Text size="xs" c="dimmed">
                            Attempt ID
                          </Text>
                          <Code block>{String(detailAttempt._id ?? "—")}</Code>
                        </div>
                      </SimpleGrid>
                    ) : (
                      <Text size="sm" c="dimmed">
                        No attempt record found for this user and mission (may still be valid for proof-only flows).
                      </Text>
                    )}
                  </SectionCard>
                </ScrollArea>
              </Tabs.Panel>

              <Tabs.Panel value="ai" pt="md" px="md" pb="md" style={{ flex: 1, minHeight: 0 }}>
                <ScrollArea h="calc(100dvh - 15rem)" type="auto" offsetScrollbars>
                  <SectionCard title="AI & signals" icon={<IconRobot size={16} />} gradient={{ from: "gray", to: "dark" }}>
                    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                      <div>
                        <Text size="xs" c="dimmed">
                          Score / confidence
                        </Text>
                        <Text size="sm" mt={4}>
                          {detailProof.aiScore != null ? detailProof.aiScore : "—"} ·{" "}
                          {detailProof.aiConfidence || "—"}
                        </Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed">
                          Decision
                        </Text>
                        <Text size="sm" mt={4}>
                          {detailProof.aiDecision || "—"}
                        </Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed">
                          OCR anchor
                        </Text>
                        <Text size="sm" mt={4}>
                          {detailProof.ocrAnchorScore != null ? detailProof.ocrAnchorScore : "—"} ·{" "}
                          {detailProof.ocrAnchorPassed == null
                            ? "—"
                            : detailProof.ocrAnchorPassed
                              ? "passed"
                              : "failed"}
                        </Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed">
                          Decision reason
                        </Text>
                        <Text size="sm" mt={4}>
                          {detailProof.aiDecisionReason || "—"}
                        </Text>
                      </div>
                    </SimpleGrid>
                    {(detailProof.aiRiskFlags?.length || 0) > 0 && (
                      <>
                        <Text size="xs" c="dimmed" mt="md">
                          Risk flags
                        </Text>
                        <Group gap="xs" mt={4}>
                          {detailProof.aiRiskFlags!.map((f) => (
                            <Badge key={f} color="red" variant="light" size="sm">
                              {f}
                            </Badge>
                          ))}
                        </Group>
                      </>
                    )}
                    {(detailProof.aiEvidence?.length || 0) > 0 && (
                      <>
                        <Text size="xs" c="dimmed" mt="md">
                          Evidence snippets
                        </Text>
                        <Stack gap={4} mt={4}>
                          {detailProof.aiEvidence!.map((ev, i) => (
                            <Text key={i} size="xs" c="dimmed">
                              • {ev}
                            </Text>
                          ))}
                        </Stack>
                      </>
                    )}
                    {detailPayload?.proof && typeof (detailPayload.proof as { aiFeedback?: string }).aiFeedback === "string" && (
                      <>
                        <Text size="xs" c="dimmed" mt="md">
                          Model feedback (raw)
                        </Text>
                        <Code block mt={4} style={{ whiteSpace: "pre-wrap", maxHeight: 200, overflow: "auto" }}>
                          {(detailPayload.proof as { aiFeedback: string }).aiFeedback}
                        </Code>
                      </>
                    )}
                  </SectionCard>
                </ScrollArea>
              </Tabs.Panel>
            </>
          )}
        </Tabs>
        </Stack>
      </Modal>
    </Container>
  );
};

export default ProofsPage;
